import React from 'react'
import { Alert, Skeleton, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'

import {
  Caption,
  Number,
  Pill,
  Tile,
  TileBody,
  TileHeader,
  TilesRow,
  TileTitle,
  ValueButton,
  ValueDivider,
  ValueRow,
} from './VcnManagementMetricTiles.styles'
import type { PillTone } from './VcnManagementMetricTiles.styles'

const NumberSkeleton = (
  <Skeleton.Input active size="small" style={{ width: 64, minWidth: 0 }} />
)

/** One reading on a tile, and the click target it exposes. */
export interface MetricValue {
  /**
   * Stable id for this click target. The caller maps it to the filters it
   * applies, so this component never knows what a number means.
   */
  key: string
  /** Provider badge beside the number. Omitted on single-number tiles. */
  pill?: { label: string; tone: PillTone }
  /** `undefined` while unknown, which renders a skeleton rather than a zero. */
  count: number | undefined
  /** Screen-reader name, since the number alone does not say what it counts. */
  ariaLabel: string
}

export interface MetricTile {
  key: string
  title: string
  tooltip: string
  /** Plain-language line under the numbers saying what is being counted. */
  caption: string
  /** One reading, or two shown side by side with a divider between them. */
  values: MetricValue[]
}

export interface VcnManagementMetricTilesProps {
  tiles: MetricTile[]
  /** Key of the active reading, or null when no metric filter is applied. */
  activeKey: string | null
  isLoading: boolean
  error?: Error | null
  onSelect: (key: string) => void
}

/**
 * The metric tiles above the grid.
 *
 * Every number rendered here is a click target that filters the grid to exactly
 * that number of rows — which is why a tile shows its provider readings rather
 * than a combined total it would have no way to filter to.
 *
 * Deliberately knows nothing about cards, benefits or filters: it renders the
 * `tiles` it is given, and hands back the `key` of whatever was clicked. That is
 * what lets Benefit Mode's benefit-unit tiles be more entries in a config array
 * rather than a second implementation.
 */
export function VcnManagementMetricTiles({
  tiles,
  activeKey,
  isLoading,
  error = null,
  onSelect,
}: VcnManagementMetricTilesProps) {
  const hasNumbers = tiles.some((tile) =>
    tile.values.some((value) => value.count !== undefined)
  )
  // An error with numbers already on screen is a failed refresh: keep them and
  // say they may be stale. With nothing shown, it is a load failure.
  const showRefreshError = Boolean(error && hasNumbers)
  const showLoadError = Boolean(error && !hasNumbers)

  const renderCount = (count: number | undefined) => {
    if (count !== undefined) return <Number>{count.toLocaleString()}</Number>
    if (error) return <Number>—</Number>
    return NumberSkeleton
  }

  return (
    <div>
      {showRefreshError && (
        <Alert
          type="warning"
          message="Could not refresh metrics — showing the last known numbers"
          description={error?.message}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      {showLoadError && (
        <Alert
          type="warning"
          message="Could not load metrics"
          description={error?.message}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      <TilesRow>
        {tiles.map((tile) => {
          // The design's box has one focused state; these tiles can hold two
          // readings that filter to different sets, so the border lights when
          // any reading on the tile is the active one.
          const isTileActive = tile.values.some(
            (value) => value.key === activeKey
          )
          return (
            <Tile key={tile.key} $active={isTileActive}>
              <TileHeader>
                <TileTitle>
                  {tile.title}
                  <Tooltip title={tile.tooltip}>
                    <InfoCircleOutlined
                      style={{ color: '#617d99', fontSize: 16 }}
                      aria-label={tile.tooltip}
                    />
                  </Tooltip>
                </TileTitle>
              </TileHeader>
              <TileBody>
                <ValueRow>
                  {tile.values.map((value, index) => {
                    // Not clickable until the number is known: a click while a
                    // skeleton showed would filter the grid to a count the admin
                    // cannot check it against.
                    const isReady = !isLoading && value.count !== undefined
                    return (
                      <React.Fragment key={value.key}>
                        {index > 0 && <ValueDivider />}
                        <ValueButton
                          type="button"
                          $active={activeKey === value.key}
                          disabled={!isReady}
                          aria-label={value.ariaLabel}
                          aria-current={
                            activeKey === value.key ? 'true' : undefined
                          }
                          onClick={() => onSelect(value.key)}
                        >
                          {value.pill && (
                            <Pill $tone={value.pill.tone}>
                              {value.pill.label}
                            </Pill>
                          )}
                          {renderCount(value.count)}
                        </ValueButton>
                      </React.Fragment>
                    )
                  })}
                </ValueRow>
                <Caption>{tile.caption}</Caption>
              </TileBody>
            </Tile>
          )
        })}
      </TilesRow>
    </div>
  )
}
